import { type FC, type ReactNode, useMemo } from "react";

import { type TechnicalDetailsData } from "~/features/productDetails/dto/PdpPageProductDataDto";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { useTranslation, type WithTranslation } from "react-i18next";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { filterTechnicalDetails } from "~/domains/userPreferences/utils/unitLists";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { MEDIA_QUERY_MIN_TABLET } from "~/common/utils/screenSizes";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";

interface DimensionsTableProps {
  data: TechnicalDetailsData;
  product: PdpPageProductData;
}

const NAME_COL_MOBILE_WIDTH = 80;
const NAME_COL_REGULAR_WIDTH = 120;
const VALUE_COL_MOBILE_WIDTH = 110;
const VALUE_COL_REGULAR_WIDTH = 165;
const DESCRIPTION_COL_MOBILE_WIDTH = 400;

interface Row {
  name: ReactNode;
  value: ReactNode;
  description: ReactNode;
}

export const TechnicalDetailsTable: FC<DimensionsTableProps> = (props) => {
  const { t } = useTranslation();
  const isMinTablet = useMediaQuery(MEDIA_QUERY_MIN_TABLET);
  const { data } = props;

  const unitMode = useUnitMode();

  const { header, tableData } = useMemo(() => {
    const filteredData = filterTechnicalDetails(data, unitMode);
    return {
      header: filteredData.title,
      tableData: transformData(t, filteredData.entries),
    };
  }, [data, unitMode]);

  const nameWidth = isMinTablet ? NAME_COL_REGULAR_WIDTH : NAME_COL_MOBILE_WIDTH;
  const valueWidth = isMinTablet ? VALUE_COL_REGULAR_WIDTH : VALUE_COL_MOBILE_WIDTH;
  const descWidth = isMinTablet ? undefined : DESCRIPTION_COL_MOBILE_WIDTH;

  const cols: ColumnDef<Row, number>[] = [
    {
      accessorKey: "name",
      size: nameWidth,
      cell: (row) => row.getValue(),
    },
    {
      accessorKey: "value",
      size: valueWidth,
      cell: (row) => row.getValue(),
    },
    {
      accessorKey: "description",
      size: descWidth,
      cell: (row) => row.getValue(),
    },
  ];

  const state = isMinTablet ? undefined : { columnPinning: { left: ["name"] } };

  return (
    <div className="scha__pdp__tech-detail-table">
      <TanStackTable
        variant={"stripes"}
        data={tableData}
        columns={cols}
        tableTitle={header}
        state={state}
        fullwidth={isMinTablet}
      />
    </div>
  );
};

function transformData(t: WithTranslation["t"], data: TechnicalDetailsData["entries"]): Row[] {
  return data.map((row) => {
    const fullAbbreviation =
      row.abbreviation + (row.abbreviationAddition ? "<sub>" + row.abbreviationAddition + "</sub>" : "");
    const name = (
      <span
        className={row.isConverted ? "f-info-color" : undefined}
        dangerouslySetInnerHTML={{ __html: fullAbbreviation }}
      />
    );

    let value;
    if (!row.values.length) {
      value = "";
    } else {
      value = (
        <>
          {row.values.map((value, index) => (
            <div key={index} className={row.isConverted ? "f-info-color" : undefined}>
              {row.relationalOperator ? row.relationalOperator : ""} <span>{`${value} ${row.unitSymbol}`}</span>
              <sup>{row.valueFootnotes.length > 1 ? row.valueFootnotes.join(", ") : row.valueFootnotes}</sup>
            </div>
          ))}
        </>
      );
    }
    const description = (
      <span className={row.isConverted ? "f-info-color" : undefined}>
        {(row.needsLocalisation ? t(row.description) : row.description) || ""}{" "}
        <sup>{row.footnotes.length > 1 ? row.footnotes.join(", ") : row.footnotes}</sup>
      </span>
    );

    return {
      name,
      value,
      description,
    };
  });
}
